import { graphqlFetch } from '@api/graphql/gql-fetch';
import {
	type MemberReview as GetReviewsForMemberReview,
	getReviewsForMemberQuery,
} from '@api/graphql/reviews/getReviewsForMember';
import { getUnifiedReviewsUrl } from '@api/reviews/url';
import { XO_SESSION_TOKEN } from '@constants/membership';
import { Logger } from '@utils/logger/logger';
import { CookieStorage } from 'cookie-storage';

interface GetReviewsForMemberResponse {
	reviews: {
		nodes: GetReviewsForMemberReview[];
	};
}

export const fetchMemberReviews = async (
	memberId: string,
	size?: number,
	page?: number,
) => {
	try {
		const headers = new Headers();
		const cookieStorage = new CookieStorage();
		const membershipSessionToken = cookieStorage.getItem(XO_SESSION_TOKEN);

		headers.append('x-tenant-id', 'tk-us');
		headers.append('membership-session-token', membershipSessionToken || '');

		const results = await graphqlFetch<GetReviewsForMemberResponse>({
			url: getUnifiedReviewsUrl(),
			query: getReviewsForMemberQuery,
			variables: {
				id: memberId,
				size: size ?? 25,
				page: page ?? 1,
				sortType: 'date',
				sort: 'desc',
			},
			headers,
		});

		if (results.errors?.length) {
			const errors = results.errors.map((error) => new Error(error.message));
			for (const error of errors) {
				Logger.error({
					log_message: 'GraphQL Error fetching reviews for member',
					errorToLog: error,
					newRelic: true,
				});
			}
			throw errors;
		}

		return results.data.reviews.nodes;
	} catch (error) {
		if (!Array.isArray(error)) {
			Logger.error({
				log_message: 'Error fetching reviews for member',
				errorToLog: error,
				newRelic: true,
			});
		}
		throw error;
	}
};

export const fetchAllMemberReviews = async (memberId: string) => {
	const allReviews: GetReviewsForMemberReview[] = [];
	const size = 60; // API enforces a max page size of 60
	let page = 1;
	let done = false;

	while (!done) {
		const results = await fetchMemberReviews(memberId, size, page);

		allReviews.push(...results);

		page++;
		done = results.length < size;
	}

	return allReviews;
};
